import {
	type GetAllContactNamesQueryVariables,
	type GetAllMembersOfQuery,
	type GetAllMembersOfQueryVariables,
	useGetAllMembersOfLazyQuery,
} from "@repo/server-api";
import { useCallback } from "react";

import { useAsyncSelectCursorPaginatedLazyQuery } from "./AsyncSelectCursorPaginatedLazyQuery";

import type { AsyncSelectSearchHook } from "../BoundAsyncSelect";

export type MemberWithEmail = {
	id: string;
	name: string;
	email: string | null;
};

export function useAsyncSelectMembersWithEmailQuery(
	contactId: string | null,
	types: GetAllContactNamesQueryVariables["types"] = null,
	andTypes: boolean = false,
	showDeleted: boolean = false,
): AsyncSelectSearchHook<MemberWithEmail> {
	const [fetchContacts] = useGetAllMembersOfLazyQuery();

	const lazyQueryWrapper = useCallback(
		async ({
			variables,
		}: {
			variables: GetAllMembersOfQueryVariables;
		}): Promise<{ data?: GetAllMembersOfQuery }> => {
			if (variables.contactId !== "") {
				return fetchContacts({ variables });
			} else {
				return Promise.resolve({
					data: undefined,
				});
			}
		},
		[fetchContacts],
	);

	const buildVariables = useCallback(
		(search: string, cursor: string | null): GetAllMembersOfQueryVariables => ({
			contactId: contactId ?? "",
			name: search || null,
			types,
			andTypes,
			showDeleted,
			first: 20,
			after: cursor,
		}),
		[contactId, types, andTypes, showDeleted],
	);

	const extractItems = (data: GetAllMembersOfQuery | undefined): MemberWithEmail[] => {
		if (!data?.allMembersOf?.nodes) return [];

		return data.allMembersOf.nodes.map((n) => {
			const emailInfo = n.additionalInformation?.find(
				(info) => info.__typename === "EmailInformation",
			);
			const email =
				emailInfo && "emailAddress" in emailInfo ? emailInfo.emailAddress : null;

			return {
				id: n.id,
				name: email ? `${n.displayName} (${email})` : n.displayName,
				email,
			};
		});
	};

	const extractEndCursor = (data: GetAllMembersOfQuery | undefined): string | null =>
		data?.allMembersOf?.pageInfo?.endCursor ?? null;

	const extractHasNextPage = (data: GetAllMembersOfQuery | undefined): boolean =>
		data?.allMembersOf?.pageInfo?.hasNextPage ?? false;

	return useAsyncSelectCursorPaginatedLazyQuery<
		GetAllMembersOfQueryVariables,
		GetAllMembersOfQuery,
		MemberWithEmail
	>(lazyQueryWrapper, buildVariables, extractItems, extractEndCursor, extractHasNextPage);
}
